/**
 * MaterialTrackingHub — 6-tab hub for the Material Management extension.
 *
 * Tab 1 — Inventory (existing bin pivot, was the whole hub before M1)
 * Tab 2 — Field Issue (M2 live — MIR Kanban + Gate Pass)
 * Tab 3 — Site Map (M6 placeholder)
 * Tab 4 — Field Tracking (M3 placeholder)
 * Tab 5 — Field Return (M4 placeholder)
 * Tab 6 — Reconciliation (M5 placeholder)
 *
 * The Inventory tab remains the canonical inventory truth — every MIR
 * approval / scan writes back to `mciEntries.receipts[]` (via the
 * MaterialUnit → Transaction hook in mtx_routes.py) so no data duplication.
 */
import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useProject } from '@/contexts/ProjectContext';
import { Warehouse, Package, ClipboardList, MapPin, Radar, PackageX, ClipboardCheck } from 'lucide-react';
import MaterialTrackingPanel from '@/components/procurement/MaterialTrackingPanel';
import FieldIssueTab from '@/components/mtx/FieldIssueTab';
import FieldTrackingTab from '@/components/mtx/FieldTrackingTab';
import FieldReturnTab from '@/components/mtx/FieldReturnTab';
import ReconciliationTab from '@/components/mtx/ReconciliationTab';
import SiteMapTab from '@/components/mtx/SiteMapTab';

const TABS = [
  { id: 'inventory',   label: 'Inventory',      icon: Package },
  { id: 'site-map',    label: 'Site Map',       icon: MapPin },
  { id: 'field-issue', label: 'Field Issue',    icon: ClipboardList },
  { id: 'field-track', label: 'Field Tracking', icon: Radar },
  { id: 'field-return', label: 'Field Return',  icon: PackageX },
  { id: 'reconcile',   label: 'Reconciliation', icon: ClipboardCheck },
];

export default function MaterialTrackingHub() {
  const { selectedProject } = useProject();
  const projectId = selectedProject?.project_id;
  const [searchParams, setSearchParams] = useSearchParams();

  // ── SWM deep-link support ─────────────────────────────────────────
  // The Scan Center's "Go to Issue MIR" link jumps here with
  // ?openMir=1&prefillMark=...&prefill*=... . Consume those params,
  // switch to the Field Issue tab and hand the prefill down to
  // FieldIssueTab → CreateMirDialog so the Site Engineer lands on the
  // New-MIR dialog with the mark already in place.
  const mirPrefill = useMemo(() => {
    if (!searchParams.get('openMir') && !searchParams.get('prefillMark')) return null;
    return {
      mark:         searchParams.get('prefillMark') || '',
      materialCode: searchParams.get('prefillCode') || '',
      description:  searchParams.get('prefillDesc') || '',
      uom:          searchParams.get('prefillUom')  || '',
      qty:          searchParams.get('prefillQty')  || '',
      workfront:    searchParams.get('prefillWorkfront') || '',
      warehouse:    searchParams.get('prefillWarehouse') || '',
    };
  }, [searchParams]);
  const [tab, setTab] = useState(mirPrefill ? 'field-issue' : 'inventory');

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="material-tracking-hub">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)' }}>
            <Warehouse className="w-7 h-7" style={{ color: '#10B981' }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Material Tracking</h1>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              IQC-Accepted stock through Issue · Transit · Install · Return · Reconciliation — one canonical unit-level chain of custody
            </p>
          </div>
        </div>

        <div className="rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          {/* Tab strip */}
          <div className="flex gap-1 px-2 pt-2 border-b overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`mtx-tab-${t.id}`}
                  className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold whitespace-nowrap"
                  style={{
                    color: active ? '#1E4E8C' : 'var(--text-muted)',
                    borderBottom: active ? '2px solid #1E4E8C' : '2px solid transparent',
                  }}>
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>

          {!projectId && (
            <div className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
              Select a project to view its warehouse inventory.
            </div>
          )}
          {projectId && tab === 'inventory'    && <MaterialTrackingPanel projectId={projectId} />}
          {projectId && tab === 'field-issue'  && <FieldIssueTab projectId={projectId}
                                                                 prefill={mirPrefill}
                                                                 onPrefillConsumed={() => setSearchParams({}, { replace: true })} />}
          {projectId && tab === 'site-map'     && <SiteMapTab projectId={projectId} />}
          {projectId && tab === 'field-track'  && <FieldTrackingTab projectId={projectId} />}
          {projectId && tab === 'field-return' && <FieldReturnTab projectId={projectId} />}
          {projectId && tab === 'reconcile'    && <ReconciliationTab projectId={projectId} />}
        </div>
      </div>
    </DashboardLayout>
  );
}
